import { z } from 'zod'
import { zodIssueSummary, type StoredJson } from '../helpers/zod-error.js'
import {
  UNDERWRITING_GATES,
  UNDERWRITING_SEVERITIES,
} from '../underwriting/gates.js'
import { LOAN_FACT_CODES } from '../underwriting/fact-codes.js'
import { UNDERWRITING_RULE_CODES } from '../underwriting/rule-codes.js'
import {
  minLoanAmountWireSchema,
  minReservesMonthsWireSchema,
} from '../underwriting/rule-catalog.js'

export const underwritingRuleProgramSummaryDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
})

const underwritingRuleDtoFields = {
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  isActive: z.boolean(),
  /** Empty = global (all programs when a loan has a program selected). */
  programs: z.array(underwritingRuleProgramSummaryDtoSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}

/**
 * `ruleCode` selects the threshold shape. A min-loan rule cannot carry
 * `{ months }` and a reserves rule cannot carry `{ amount }`.
 */
export const underwritingRuleDtoSchema = z.discriminatedUnion('ruleCode', [
  z.object({
    ...underwritingRuleDtoFields,
    ruleCode: z.literal('MIN_RESERVES_MONTHS'),
    threshold: minReservesMonthsWireSchema,
  }),
  z.object({
    ...underwritingRuleDtoFields,
    ruleCode: z.literal('MIN_LOAN_AMOUNT'),
    threshold: minLoanAmountWireSchema,
  }),
])

export type UnderwritingRuleDto = z.infer<typeof underwritingRuleDtoSchema>

export const underwritingRuleListDtoSchema = z.object({
  rules: z.array(underwritingRuleDtoSchema),
  total: z.number().int().nonnegative(),
})

export type UnderwritingRuleListDto = z.infer<
  typeof underwritingRuleListDtoSchema
>

export const underwritingFindingDtoSchema = z.object({
  ruleInstanceId: z.string().uuid(),
  ruleCode: z.enum(UNDERWRITING_RULE_CODES),
  severity: z.enum(UNDERWRITING_SEVERITIES),
  gate: z.enum(UNDERWRITING_GATES),
  factCode: z.enum(LOAN_FACT_CODES),
  actual: z.number().nullable(),
  required: z.number(),
  message: z.string(),
  unavailableReason: z.string().nullable(),
  /**
   * True when this finding blocks the evaluated gate.
   * Currently always equals `severity === 'BLOCK'`; kept for clients that
   * branch on blocks without re-deriving severity.
   */
  blocks: z.boolean(),
})

export type UnderwritingFindingDto = z.infer<
  typeof underwritingFindingDtoSchema
>

export const underwritingFindingsDtoSchema = z.object({
  findings: z.array(underwritingFindingDtoSchema),
  blocked: z.boolean(),
  /** Loan has no program — structured rules do not apply. */
  skippedNoProgram: z.boolean(),
  /** Loan has no lender — structured rules do not apply. */
  skippedNoLender: z.boolean(),
  /**
   * Rule instance ids suppressed from the active list because a prior
   * guideline override on this loan covered them (display gate only).
   */
  suppressedByOverrideCount: z.number().int().nonnegative(),
})

export type UnderwritingFindingsDto = z.infer<
  typeof underwritingFindingsDtoSchema
>

/**
 * Audit metadata written when a status change overrides a blocking guideline.
 * Other audit metadata is not this shape and is ignored.
 */
export const guidelineOverrideMetadataSchema = z.object({
  guidelineOverride: z.literal(true),
  findings: z.array(
    z.object({
      ruleInstanceId: z.string().uuid(),
      ruleCode: z.enum(UNDERWRITING_RULE_CODES),
      severity: z.enum(UNDERWRITING_SEVERITIES),
      gate: z.enum(UNDERWRITING_GATES),
      message: z.string(),
      actual: z.number().nullable(),
      required: z.number(),
      unavailableReason: z.string().nullable(),
    }),
  ),
})

const guidelineOverrideClaimSchema = z.object({
  guidelineOverride: z.literal(true),
})

/**
 * Rule instance ids covered by a guideline override. Metadata that is not an
 * override returns an empty list. An override whose findings are malformed
 * throws.
 */
export function overriddenRuleIds(metadata: StoredJson | null): string[] {
  if (!guidelineOverrideClaimSchema.safeParse(metadata).success) return []
  const parsed = guidelineOverrideMetadataSchema.safeParse(metadata)
  if (!parsed.success) {
    throw new Error(
      `Invalid guideline override metadata: ${zodIssueSummary(parsed.error)}`,
    )
  }
  return parsed.data.findings.map((finding) => finding.ruleInstanceId)
}
